import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { FaEthereum, FaArrowLeft, FaShare, FaEdit, FaTrash } from 'react-icons/fa';
import { CubeIcon, ShieldCheckIcon, TagIcon, UserIcon } from '@heroicons/react/24/outline';
import api from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';
import ImmutableMintButton from '../components/nft/ImmutableMintButton';
import NFTPurchaseButton from '../components/nft/NFTPurchaseButton';

// Helper functions for rarity styling
const getRarityColor = (rarity) => {
  switch (rarity?.toLowerCase()) {
    case 'legendary':
      return 'from-yellow-500 to-amber-600';
    case 'epic':
      return 'from-purple-500 to-violet-600';
    case 'rare':
      return 'from-blue-500 to-indigo-600';
    case 'uncommon':
      return 'from-green-500 to-emerald-600';
    default:
      return 'from-gray-500 to-gray-600';
  }
};

const getRarityGlow = (rarity) => {
  switch (rarity?.toLowerCase()) {
    case 'legendary':
      return 'shadow-yellow-500/50';
    case 'epic':
      return 'shadow-purple-500/50';
    case 'rare':
      return 'shadow-blue-500/50';
    case 'uncommon':
      return 'shadow-green-500/50';
    default:
      return 'shadow-gray-500/50';
  }
};

export default function NFTDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isConnected } = useAuth();
  const [nft, setNft] = useState(null);
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMinted, setIsMinted] = useState(false);

  useEffect(() => {
    const fetchNFTDetails = async () => {
      try {
        setLoading(true);
        console.log(`Fetching NFT details for ID: ${id}`);

        // Fetch NFT data
        const nftResponse = await api.get(API_ENDPOINTS.nft.nft(id));
        console.log('NFT response:', nftResponse);

        if (!nftResponse) {
          throw new Error('Failed to fetch NFT details');
        }

        setNft(nftResponse);

        // Set initial minted status
        setIsMinted(nftResponse.status === 'minted' || nftResponse.status === 'transferred' || nftResponse.status === 'sold' || nftResponse.owner);

        // Fetch collection data if we have a collectionId
        if (nftResponse.collectionId) {
          const collectionId = typeof nftResponse.collectionId === 'object'
            ? nftResponse.collectionId._id
            : nftResponse.collectionId;

          console.log(`Fetching collection details for ID: ${collectionId}`);
          const collectionResponse = await api.get(API_ENDPOINTS.nft.collection(collectionId));
          console.log('Collection response:', collectionResponse);

          if (collectionResponse) {
            setCollection(collectionResponse);
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching NFT details:', error);
        setError('Failed to load NFT details. Please try again later.');
        setLoading(false);
        toast.error('Failed to load NFT details');
      }
    };

    if (id) {
      fetchNFTDetails();
    }
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url)
      .then(() => {
        toast.success('Link copied to clipboard');
      })
      .catch(() => {
        toast.error('Failed to copy link');
      });
  };

  const handleBuyOrMint = () => {
    if (!isConnected) {
      toast.error('Please connect your wallet first');
      return;
    }

    // This would be replaced with actual minting/buying logic
    toast.success('This feature is coming soon!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex justify-center items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  if (error || !nft) {
    return (
      <div className="min-h-screen bg-dark flex flex-col justify-center items-center p-4">
        <div className="text-2xl text-red-400 mb-4">Error Loading NFT</div>
        <p className="text-gray-400 mb-6">{error || 'NFT not found'}</p>
        <button
          onClick={handleBack}
          className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors flex items-center"
        >
          <FaArrowLeft className="mr-2" /> Go Back
        </button>
      </div>
    );
  }

  const rarityColor = getRarityColor(nft.rarity);
  const rarityGlow = getRarityGlow(nft.rarity);

  return (
    <div className="min-h-screen bg-dark">
      {/* Back button, admin controls, and share */}
      <div className="max-w-7xl mx-auto px-4 py-6 flex justify-between items-center">
        <button
          onClick={handleBack}
          className="flex items-center text-gray-300 hover:text-white transition-colors"
        >
          <FaArrowLeft className="mr-2" /> Back
        </button>
        <div className="flex items-center space-x-4">
          {user?.isAdmin && (
            <div className="flex space-x-4">
              <button
                onClick={() => navigate(`/nft/${nft._id}/edit`)}
                className="flex items-center text-violet-400 hover:text-violet-300 transition-colors"
              >
                <FaEdit className="mr-2" /> Edit NFT
              </button>
              <button
                onClick={async () => {
                  if (window.confirm('Are you sure you want to delete this NFT? This action cannot be undone.')) {
                    try {
                      const deleteEndpoint = API_ENDPOINTS.admin.nft.deleteNFT(nft._id);
                      console.log('Delete endpoint:', deleteEndpoint);

                      // Add admin headers for this request
                      const config = {
                        headers: {
                          'X-Admin-Request': 'true',
                          'X-Debug-Admin': 'true'
                        }
                      };

                      const response = await api.delete(deleteEndpoint, config);
                      console.log('Delete response:', response);
                      toast.success('NFT deleted successfully');
                      navigate('/nft-collection');
                    } catch (error) {
                      console.error('Error deleting NFT:', error);

                      // Check for specific error about minted NFTs
                      if (error.message && error.message.includes('Cannot delete NFT that has been minted')) {
                        toast.error('Cannot delete NFT that has been minted. Only draft NFTs can be deleted.');
                      } else {
                        toast.error('Failed to delete NFT: ' + (error.message || 'Unknown error'));
                      }
                    }
                  }
                }}
                className="flex items-center text-red-400 hover:text-red-300 transition-colors"
              >
                <FaTrash className="mr-2" /> Delete NFT
              </button>

              <button
                onClick={async () => {
                  if (window.confirm('WARNING: This will FORCE DELETE the NFT directly from the database regardless of its status. This action cannot be undone. Are you absolutely sure?')) {
                    try {
                      // Use the force delete endpoint
                      const forceDeleteEndpoint = API_ENDPOINTS.admin.nft.forceDeleteNFT(nft._id);
                      console.log('Force delete endpoint:', forceDeleteEndpoint);

                      // Add special headers for force delete
                      const forceConfig = {
                        headers: {
                          'X-Admin-Request': 'true',
                          'X-Debug-Admin': 'true',
                          'X-Database-Override': 'true'
                        }
                      };

                      console.log('Force delete config:', forceConfig);

                      const forceResponse = await api.delete(forceDeleteEndpoint, forceConfig);
                      console.log('Force delete response:', forceResponse);
                      toast.success('NFT force deleted successfully from database');
                      navigate('/nft-collection');
                    } catch (forceError) {
                      console.error('Error force deleting NFT from database:', forceError);
                      toast.error('Failed to force delete NFT: ' + (forceError.message || 'Unknown error'));
                    }
                  }
                }}
                className="flex items-center text-orange-500 hover:text-orange-400 transition-colors"
              >
                <FaTrash className="mr-2" /> Force Delete
              </button>
            </div>
          )}
          <button
            onClick={handleShare}
            className="flex items-center text-gray-300 hover:text-white transition-colors"
          >
            <FaShare className="mr-2" /> Share
          </button>
        </div>
      </div>

      {/* NFT Details */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* NFT Image */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="relative rounded-xl overflow-hidden aspect-square shadow-2xl"
            >
              <img
                src={nft.image || "/logo.png"}
                alt={nft.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/logo.png";
                }}
              />
              <div className={`absolute top-4 right-4 text-white px-3 py-1 rounded-full text-sm font-semibold bg-gradient-to-r ${rarityColor} shadow-lg ${rarityGlow} z-20`}>
                {nft.rarity}
              </div>

              {/* Status badge for minted NFTs */}
              {nft.status && nft.status !== 'draft' && (
                <div className="absolute top-4 left-4 bg-green-900 text-green-300 px-3 py-1 rounded-full text-xs font-semibold z-20">
                  {nft.status.charAt(0).toUpperCase() + nft.status.slice(1)}
                </div>
              )}
            </motion.div>
          </div>

          {/* NFT Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">{nft.name}</h1>

            {/* Description */}
            <div className="mb-6 p-4 bg-dark-lighter rounded-xl border border-violet-500/20">
              <h3 className="text-lg font-semibold text-white mb-3">Description</h3>
              <p className="text-gray-300 whitespace-pre-wrap leading-relaxed">{nft.description}</p>
            </div>

            {/* Collection Info */}
            {collection && (
              <div className="mb-6 p-4 bg-dark-lighter rounded-xl border border-violet-500/20">
                <h3 className="text-lg font-semibold text-white mb-2 flex items-center">
                  <CubeIcon className="h-5 w-5 mr-2 text-violet-500" />
                  Collection
                </h3>
                <div className="flex items-center">
                  {collection.logoImage && (
                    <img
                      src={collection.logoImage}
                      alt={collection.name}
                      className="w-10 h-10 rounded-full mr-3 object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/logo.png";
                      }}
                    />
                  )}
                  <div>
                    <p className="text-white font-semibold">{collection.name}</p>
                    <p className="text-sm text-gray-400">
                      {collection.mintedCount} / {collection.totalSupply} minted
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Price and Action */}
            <div className="bg-dark-lighter rounded-xl p-6 mb-6 border border-violet-500/20">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <p className="text-gray-400 text-sm">Price</p>
                  <div className="flex items-center text-white text-2xl font-bold">
                    {nft.currency === 'ETH' ? (
                      <FaEthereum className="mr-1" />
                    ) : (
                      <img src="/imx.svg" alt="IMX" className="w-6 h-6 mr-1" />
                    )}
                    <span>{nft.price} {nft.currency}</span>
                  </div>

                  {/* Edition information */}
                  {nft.totalSupply > 1 && (
                    <div className="mt-2 text-sm text-gray-300">
                      <span className="font-semibold">Edition:</span> {nft.mintedSupply || 0} of {nft.totalSupply} minted
                    </div>
                  )}
                </div>

                {/* Only show purchase button if NFT is available */}
                {(nft.status === 'draft' || (nft.totalSupply > 1 && (nft.mintedSupply || 0) < nft.totalSupply)) && (
                  user?.isAdmin ? (
                    <ImmutableMintButton
                      nft={nft}
                      onSuccess={(updatedNft) => {
                        setNft(updatedNft);
                        setIsMinted(updatedNft.status === 'minted');
                      }}
                      className="glow-button px-6 py-3 text-lg"
                    />
                  ) : (
                    <NFTPurchaseButton
                      nft={nft}
                      onSuccess={(updatedNft) => {
                        setNft(updatedNft);
                        setIsMinted(updatedNft.status === 'minted');
                      }}
                      className="glow-button px-6 py-3 text-lg"
                    />
                  )
                )}

                {/* Show sold out message if all editions are minted */}
                {nft.totalSupply > 1 && (nft.mintedSupply || 0) >= nft.totalSupply && (
                  <div className="px-6 py-3 bg-red-900/50 text-red-300 rounded-lg font-semibold">
                    Sold Out
                  </div>
                )}
              </div>

              {/* Owner info if minted */}
              {nft.owner && (
                <div className="flex items-center text-gray-300">
                  <UserIcon className="h-4 w-4 mr-2" />
                  <span className="text-sm">
                    Owned by: {nft.owner.substring(0, 6)}...{nft.owner.substring(nft.owner.length - 4)}
                  </span>
                </div>
              )}
            </div>

            {/* Attributes */}
            {nft.attributes && nft.attributes.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                  <TagIcon className="h-5 w-5 mr-2 text-violet-500" />
                  Attributes
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {nft.attributes.map((attr, index) => (
                    <div key={index} className="bg-dark-lighter rounded-lg p-3 border border-violet-500/10">
                      <p className="text-gray-400 text-sm">{attr.trait_type}</p>
                      <div className="flex justify-between items-center">
                        <p className="text-white font-semibold">{attr.value}</p>
                        {typeof attr.value === 'number' && (
                          <div className="w-16 h-2 bg-dark-400 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-violet-500 to-purple-600"
                              style={{ width: `${Math.min(attr.value, 100)}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Blockchain Info */}
            <div className="bg-dark-lighter rounded-xl p-4 border border-violet-500/20">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
                <ShieldCheckIcon className="h-5 w-5 mr-2 text-violet-500" />
                Blockchain Details
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3">
                  <p className="text-gray-400 text-sm">Network</p>
                  <p className="text-white font-semibold flex items-center">
                    <CubeIcon className="h-4 w-4 mr-1" /> Immutable X
                  </p>
                </div>

                {nft.tokenId && (
                  <div className="p-3">
                    <p className="text-gray-400 text-sm">Token ID</p>
                    <p className="text-white font-semibold">{nft.tokenId}</p>
                  </div>
                )}

                {nft.mintedAt && (
                  <div className="p-3">
                    <p className="text-gray-400 text-sm">Minted On</p>
                    <p className="text-white font-semibold">
                      {new Date(nft.mintedAt).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {nft.mintTxHash && (
                  <div className="p-3">
                    <p className="text-gray-400 text-sm">Transaction</p>
                    <p className="text-white font-semibold truncate">
                      {nft.mintTxHash.substring(0, 10)}...
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
